import { useCallback, useEffect, useRef } from 'react'
import { streamAuthorCommission } from '@/lib/api'
import { useAuthorCommissioningStore } from '@/stores/useAuthorCommissioningStore'
import { CommissioningTranscript } from './components/CommissioningTranscript'
import { CommissioningInput } from './components/CommissioningInput'
import './styles/author.css'

export function CommissioningSurface() {
  const {
    sessionId,
    phase,
    proposalText,
    isStreaming,
    setSessionId,
    setPhase,
    startUserTurn,
    startStudioTurn,
    appendStudioText,
    finishStudioTurn,
    setThinking,
    appendThinkingDelta,
    clearThinking,
    setStreaming,
    setProposalText,
    setAuthorId,
    setError,
  } = useAuthorCommissioningStore()

  // Tracks whether we've already auto-fired the "go" message for this entry
  // into proposing. Prevents loops if the proposal is slow / fails.
  const proposalAutoFireFiredRef = useRef(false)

  const sendMessage = useCallback(
    async (
      content: string,
      options?: {
        intent?: 'accept' | 'pushback'
        visibleInChat?: boolean
        // When true, this turn is expected to produce a proposal (rendered
        // as a card, not a chat bubble). Skip pre-allocating the bubble.
        expectProposal?: boolean
      },
    ) => {
      if (isStreaming) return

      const visible = options?.visibleInChat ?? true
      const expectProposal = options?.expectProposal ?? false
      if (visible) startUserTurn(content)
      setStreaming(true)

      // Pre-allocate a studio chat bubble unless we're expecting the response
      // to render as a proposal card.
      const studioMessageId = expectProposal ? null : startStudioTurn()
      let accumulatedResponse = ''
      let studioBubbleNeeded = !expectProposal

      try {
        await streamAuthorCommission(
          {
            session_id: sessionId,
            content,
            run_mode: 'deep',
            intent: options?.intent ?? null,
          },
          {
            onThinkingStart: () => setThinking(true),
            onThinkingDelta: (d) => appendThinkingDelta(d),
            onThinkingComplete: () => clearThinking(),
            onTextDelta: (d) => {
              accumulatedResponse += d
              if (studioMessageId) appendStudioText(studioMessageId, d)
            },
            onStudioDone: (output) => {
              if (!studioMessageId) return
              if (accumulatedResponse.length === 0 && output.response) {
                finishStudioTurn(studioMessageId, output.response)
              } else {
                finishStudioTurn(studioMessageId)
              }
              if (output.session_id) setSessionId(output.session_id)
            },
            onProposalDone: (output) => {
              studioBubbleNeeded = false
              if (output.proposal_text) setProposalText(output.proposal_text)
              else if (output.response) setProposalText(output.response)
              if (output.session_id) setSessionId(output.session_id)
            },
            onContractDone: (output) => {
              if (studioMessageId && output.response) {
                if (accumulatedResponse.length === 0) {
                  finishStudioTurn(studioMessageId, output.response)
                } else {
                  finishStudioTurn(studioMessageId)
                }
              }
              if (output.author_id) setAuthorId(output.author_id)
            },
            onCommissioningDone: (output) => {
              if (output.session_id) setSessionId(output.session_id)
              setPhase(output.phase as never)
              if (output.proposal_text) setProposalText(output.proposal_text)
              if (output.author_id) setAuthorId(output.author_id)
            },
            onError: (err) => {
              setError(err)
              if (studioMessageId) {
                finishStudioTurn(studioMessageId, `(error: ${err})`)
                studioBubbleNeeded = true
              }
            },
          },
        )

        // Cleanup: if a chat bubble was pre-allocated but the proposal
        // arrived instead, remove it from the transcript.
        if (studioMessageId && !studioBubbleNeeded) {
          useAuthorCommissioningStore.setState((s) => ({
            messages: s.messages.filter((m) => m.id !== studioMessageId),
          }))
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        setError(message)
        if (studioMessageId) {
          finishStudioTurn(studioMessageId, `(error: ${message})`)
        }
      } finally {
        clearThinking()
        setStreaming(false)
      }
    },
    [
      sessionId,
      isStreaming,
      startUserTurn,
      startStudioTurn,
      appendStudioText,
      finishStudioTurn,
      setThinking,
      appendThinkingDelta,
      clearThinking,
      setStreaming,
      setSessionId,
      setPhase,
      setProposalText,
      setAuthorId,
      setError,
    ],
  )

  // Auto-fire when phase advances into proposing without a proposal yet.
  // The "go" turn is hidden from chat; the proposal will arrive as a card.
  useEffect(() => {
    if (phase !== 'proposing') {
      proposalAutoFireFiredRef.current = false
      return
    }
    if (proposalText) return
    if (isStreaming) return
    if (proposalAutoFireFiredRef.current) return
    proposalAutoFireFiredRef.current = true
    sendMessage('go', { visibleInChat: false, expectProposal: true })
  }, [phase, proposalText, isStreaming, sendMessage])

  const acceptProposal = useCallback(() => {
    sendMessage('accept', { intent: 'accept', visibleInChat: false })
  }, [sendMessage])

  const inputDisabled =
    isStreaming || phase === 'proposing' || phase === 'contracted'

  return (
    <div className="author-surface">
      <div className="author-surface__scroll">
        <div className="author-surface__column">
          <CommissioningTranscript
            onAccept={acceptProposal}
            acceptDisabled={isStreaming}
          />
        </div>
      </div>
      <CommissioningInput disabled={inputDisabled} onSubmit={sendMessage} />
    </div>
  )
}
